import * as React from "react";
import { GlobalSearch } from "./GlobalSearch";
import {
  GlobalSearchContext,
  type GlobalSearchContextValue,
} from "./context";

/**
 * Mounts the global-search overlay once, high in the tree (the sidebar agent
 * mounts this in PlatformWithSidebar so it wraps all routes), and registers the
 * global ⌘/Ctrl+K shortcut. Consumers call `useGlobalSearch().open()` from the
 * sidebar search row, the home header search button, and the mobile search icon.
 */
export function GlobalSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  // Global ⌘/Ctrl+K toggles the palette. (Esc-to-close is handled by the
  // overlay itself while it is open.)
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const value = React.useMemo<GlobalSearchContextValue>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen]
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      <GlobalSearch open={isOpen} onClose={close} />
    </GlobalSearchContext.Provider>
  );
}
